import { TrashIcon } from "@heroicons/react/24/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import AddToToolboxBeforeIcon from "../../../assets/icons/AddToToolboxBefore.svg";
import { useDeleteResponse } from "../../../api/mutations";
import {
  useGetPageBookmarksGroupedLessons,
  useGetResponsesByPages,
} from "../../../api/queries";
import Button from "../../../componentLibrary/Button";
import { NewlyAddedBadgeWrapper } from "../../../componentLibrary/NewlyAddedBadge";
import PsychButton from "../../../componentLibrary/PsychButton";
import ResponseModal from "../../../componentLibrary/ResponseModal";
import Spinner from "../../../componentLibrary/Spinner";
import { useNotification } from "../../../hooks/useNotification";
import FavoriteButton from "../../../components/FavoriteButton";
import { BookmarkI, LessonI, ResponseI } from "../../../types";
import ActivityModal from "../../ActivityPage/ActivityModal";
import { ErrorDisplay } from "../../../utils/ErrorHandling";

export interface GroupedPageBookmarkLessonI {
  _id: string;
  lesson: LessonI;
  bookmarks: BookmarkI[];
}

export default function FavoritesListGrouped({
  topicId,
  onClickCallback,
}: {
  topicId?: string;
  onClickCallback?: (bookmark: BookmarkI) => void;
}) {
  const queryClient = useQueryClient();
  const { notify } = useNotification();
  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedLessonId, setSelectedLessonId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedResponses, setSelectedResponses] = useState<ResponseI[]>([]);
  const [isResponseModalOpen, setIsResponseModalOpen] = useState(false);
  const [currentCarouselIndex, setCurrentCarouselIndex] = useState(0);
  const [responseToDelete, setResponseToDelete] = useState<string | null>(null);

  const {
    data: favorites,
    isError,
    isLoading,
    error,
  } = useGetPageBookmarksGroupedLessons(topicId);

  // Get all page IDs from bookmarks for efficient batch query
  const allPageIds =
    favorites?.flatMap(({ bookmarks }) =>
      bookmarks.map((bookmark) => bookmark.page?._id).filter(Boolean),
    ) || [];

  const {
    data: responsesByPage,
    isLoading: responsesLoading,
    isError: isResponsesError,
    error: responsesError,
  } = useGetResponsesByPages(allPageIds);

  const deleteMutation = useDeleteResponse({
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["responses"],
      });
      notify("success", "Response deleted");
      handleCloseResponseModal();
    },
    onError: () => {
      notify("error", "Something went wrong");
    },
  });

  // Function to check if a page has responses - now O(1) lookup
  const pageHasResponses = (pageId: string) => {
    if (!responsesByPage || !pageId) return false;
    return responsesByPage[pageId] && responsesByPage[pageId].length > 0;
  };

  // Function to get responses for a specific page - now O(1) lookup
  const getResponsesForPage = (pageId: string): ResponseI[] => {
    if (!responsesByPage || !pageId) return [];
    return responsesByPage[pageId] || [];
  };

  // Function to open response modal for a specific page
  const openResponseModal = (pageId: string) => {
    const responses = getResponsesForPage(pageId);
    if (responses.length > 0) {
      setSelectedResponses(responses);
      setCurrentCarouselIndex(0); // Reset to first item when opening
      setIsResponseModalOpen(true);
    }
  };

  // Function to handle carousel changes
  const handleCarouselChange = (index: number) => {
    setCurrentCarouselIndex(index);
  };

  // Function to close response modal
  const handleCloseResponseModal = () => {
    setIsResponseModalOpen(false);
    setCurrentCarouselIndex(0);
    setResponseToDelete(null);
  };

  const handleDelete = (id: string) => {
    setResponseToDelete(id);
  };

  const confirmDelete = () => {
    if (responseToDelete) {
      deleteMutation.mutate(responseToDelete);
    }
  };

  const cancelDelete = () => {
    setResponseToDelete(null);
  };

  const getCurrentResponseId = () => {
    if (!selectedResponses || selectedResponses.length === 0) return "";
    return selectedResponses[currentCarouselIndex]?._id || "";
  };

  if (isLoading || responsesLoading) return <Spinner className="mx-auto" />;
  if (isError || isResponsesError)
    return <ErrorDisplay error={error || responsesError} messageSource="api" />;

  return (
    <div className="space-y-4 w-full">
      {favorites.length > 0 ? (
        <>
          {favorites.map(({ lesson, bookmarks }, index) => {
            return (
              <div key={lesson._id}>
                <h3 className="mb-2">{lesson.title}</h3>
                <div>
                  {bookmarks.map((bookmark, index1) => (
                    <div key={bookmark._id} className="mb-3">
                      <NewlyAddedBadgeWrapper
                        show={!bookmark.lastActive}
                        size="small"
                      >
                        <PsychButton
                          className="w-full"
                          onClick={() => {
                            setSelectedPageId(bookmark.page?._id);
                            setSelectedLessonId(bookmark.lesson?._id);
                            setIsModalOpen(true);
                            onClickCallback?.(bookmark);
                          }}
                          text={bookmark.page?.title}
                          favButton={
                            <FavoriteButton
                              page={bookmark.page}
                              confirmDelete={true}
                              showTutorial={index === 0 && index1 === 0}
                            />
                          }
                        />
                      </NewlyAddedBadgeWrapper>
                      {bookmark.page?._id &&
                        pageHasResponses(bookmark.page._id) && (
                          <div className="mt-0 flex justify-end pr-2">
                            <button
                              onClick={() => {
                                openResponseModal(bookmark.page!._id!);
                              }}
                              className="w-1/2 text-sm text-white hover:text-white border bg-electrum hover:bg-electrum-250 px-3 py-0.5 rounded-bl-lg rounded-br-lg border-t-0"
                            >
                              View{" "}
                              {getResponsesForPage(bookmark.page._id).length}{" "}
                              Response
                              {getResponsesForPage(bookmark.page._id).length !==
                              1
                                ? "s"
                                : ""}
                            </button>
                          </div>
                        )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          {selectedPageId && (
            <ActivityModal
              pageId={selectedPageId}
              isModalOpen={isModalOpen}
              setIsModalOpen={setIsModalOpen}
              moduleId={topicId}
              lessonId={selectedLessonId}
            />
          )}
          <ResponseModal
            isOpen={isResponseModalOpen}
            onClose={handleCloseResponseModal}
            responses={selectedResponses}
            currentResponseIndex={currentCarouselIndex}
            onResponseChange={handleCarouselChange}
            showTopicInfo={true}
            footer={
              responseToDelete ? (
                <div className="space-y-3">
                  <div className="text-center text-sm text-gray-600">
                    Are you sure you want to delete your answers shown above?
                  </div>
                  <div className="flex justify-center space-x-4">
                    <Button
                      size="sm"
                      variant="alternative"
                      onClick={cancelDelete}
                    >
                      Cancel
                    </Button>
                    <Button
                      size="sm"
                      variant="cautionary"
                      onClick={confirmDelete}
                    >
                      Yes, Delete
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex justify-between items-center">
                  <Button.Circle
                    variant="cautionary"
                    className="p-1"
                    onClick={() => handleDelete(getCurrentResponseId())}
                  >
                    <TrashIcon className="w-5 h-5" />
                  </Button.Circle>
                  <Button
                    size="sm"
                    variant="alternative"
                    onClick={handleCloseResponseModal}
                  >
                    Close
                  </Button>
                </div>
              )
            }
          />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center mt-4">
          <h3 className="text-electrum">To add skills, explore topics.</h3>
          <p className="m-4 text-center">
            When you find a page containing a skill you like, click the toolbox
            icon{" "}
            <img
              src={AddToToolboxBeforeIcon}
              alt="add to toolbox icon"
              className="h-6 w-6 inline"
            />{" "}
            to add it to this page.
          </p>
        </div>
      )}
    </div>
  );
}
